import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type { ChannelDelayThresholdRow } from "@/types/preferences_types";

// ============================================
// API 응답 타입
// ============================================

interface ChannelDelayThresholdsResponse {
  rows: ChannelDelayThresholdRow[];
}

// ============================================
// API 함수들
// ============================================

/**
 * 채널별 수집 지연 시간 조회
 */
async function fetchChannelDelayThresholds(): Promise<ChannelDelayThresholdRow[]> {
  const response = await api.get<ChannelDelayThresholdsResponse>("/channel-delay-thresholds/");
  return response.rows;
}

/**
 * 채널별 수집 지연 시간 저장
 */
async function saveChannelDelayThresholds(rows: ChannelDelayThresholdRow[]): Promise<void> {
  return api.put<void>("/channel-delay-thresholds/", rows);
}

// ============================================
// Query 훅들
// ============================================

/**
 * 채널별 수집 지연 시간 조회 Query
 */
export function useChannelDelayThresholdsQuery() {
  return useQuery({
    queryKey: queryKeys.channelDelayThresholds.list(),
    queryFn: fetchChannelDelayThresholds,
  });
}

// ============================================
// Mutation 훅들
// ============================================

/**
 * 채널별 수집 지연 시간 저장 Mutation
 */
export function useSaveChannelDelayThresholdsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rows: ChannelDelayThresholdRow[]) => saveChannelDelayThresholds(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channelDelayThresholds.list() });
      toast({
        title: "저장 완료",
        description: "채널별 수집 지연 시간이 저장되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "저장 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
